/**
 * Top 20 Program Landing Pages targeted by the `ab_project_outcomes_first_plp`
 * experiment. Slugs are sourced from the Project-First PLP ND List and must
 * match the values in `catalog-data.json` so the CF Worker route-swap serves
 * the challenger variant only for these programs.
 */
export const EXPERIMENT_KEY = 'ab_project_outcomes_first_plp';

/** Design token shared by every Figma module in this experiment. No v2 theme equivalent. */
export const CARD_BORDER_COLOR = '#F2F2F2';

/**
 * Figma `body/xsm`. The theme's `xs` token is 10px, which reads too small
 * against the design and fails contrast at the caption grey.
 */
export const CAPTION_FONT_SIZE = '12px';

/**
 * How many reviews the carousel pulls on the client. The server payload only
 * holds one page, which runs out after a couple of arrow clicks.
 */
export const CAROUSEL_REVIEW_COUNT = 15;

/** The modal lays reviews out as a 4-across grid, three rows deep. */
export const MODAL_REVIEW_PAGE_SIZE = 12;

/**
 * Attribution mark required on every Trustpilot review card. Exported from the
 * Figma module and downscaled to 3x the 67x16 slot it renders into.
 */
export const TRUSTPILOT_LOGO = {
  src: '/images/experiments/ab_project_outcomes_first_plp/trustpilot-logo.png',
  alt: 'Trustpilot',
  width: 67,
  height: 16,
} as const;

export const GRADUATE_OUTCOMES_PERCENT = 96;

export const GRADUATE_OUTCOMES_URL = '/blog/is-udacity-worth-it-what-graduate-outcomes-actually-show/';

/**
 * Salary figures are estimates, so this hedge has to ship alongside every
 * rendered salary tier.
 */
export const SALARY_DISCLAIMER = 'Salary estimates based on public market data. Individual results vary.';

export type Top20Program = {
  key: string;
  slug: string;
};

export const TOP_20_PROGRAMS: Top20Program[] = [
  { key: 'nd900', slug: 'agentic-ai--nd900' },
  { key: 'nd089', slug: 'ai-programming-python-nanodegree--nd089' },
  { key: 'nd608', slug: 'generative-ai--nd608' },
  { key: 'nd770', slug: 'ai-powered-software-engineer--nd770' },
  { key: 'nd881', slug: 'ai-trading-strategies--nd881' },
  { key: 'nd002', slug: 'data-analyst-nanodegree--nd002' },
  { key: 'nd036', slug: 'product-manager-nanodegree--nd036' },
  { key: 'nd000', slug: 'intro-to-programming-nanodegree--nd000' },
  { key: 'nd898', slug: 'ai-artificial-intelligence-nanodegree--nd898' },
  { key: 'nd018', slug: 'digital-marketing-nanodegree--nd018' },
  { key: 'nd025', slug: 'data-scientist-nanodegree--nd025' },
  { key: 'nd101', slug: 'deep-learning--nd101' },
  { key: 'nd027', slug: 'data-engineering-with-aws--nd027' },
  { key: 'nd209', slug: 'robotics-software-engineer--nd209' },
  { key: 'nd104', slug: 'programming-for-data-science-nanodegree--nd104' },
  { key: 'nd213', slug: 'c-plus-plus-nanodegree--nd213' },
  { key: 'nd098', slug: 'business-analytics-nanodegree--nd098' },
  { key: 'nd189', slug: 'aws-machine-learning-engineer-nanodegree--nd189' },
  { key: 'nd256', slug: 'data-structures-and-algorithms-nanodegree--nd256' },
  { key: 'nd0821', slug: 'machine-learning-dev-ops-engineer-nanodegree--nd0821' },
];

export const TOP_20_PROGRAM_KEYS = TOP_20_PROGRAMS.map((p) => p.key);

export const TOP_20_PROGRAM_SLUGS = TOP_20_PROGRAMS.map((p) => p.slug);

export const TOP_20_SLUG_REGEX = new RegExp(
  `^/course/(${TOP_20_PROGRAM_SLUGS.map((slug) => slug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})$`
);

export function isTop20ProgramKey(key: string | undefined | null): boolean {
  return Boolean(key && TOP_20_PROGRAM_KEYS.includes(key));
}
